import React, { useState } from "react";
import Button from "../components/Button";
import {
  apiGetAudioId,
  apiGetCookies,
  apiGetGpuId,
  apiGetUniqueId,
  apiGetUserAgent,
  apiGetUserIp,
} from "../services/apiService";
import { BalloonDrop, ProgressBar } from "../components";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BACKEND_API;

const Fingerprint = () => {
  const [myId, setMyId] = useState({ myId: "" });
  const [userIp, setUserIp] = useState({});
  const [cookies, setCookies] = useState({});
  const [userAgent, setUserAgent] = useState("");
  const [uniqueId, setUniqueId] = useState("");
  const [gpuObject, setGpuObject] = useState({});
  const [audioId, setAudioId] = useState("");
  const [page, setPage] = useState(1);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isFinished, setIsFinshed] = useState(false);

  const navigate = useNavigate();

  const pages = 8;

  const nextPage = () => {
    setIsDisabled(true);
    if (page === pages) return;
    setPage(page + 1);
  };

  const prevPage = () => {
    if (page === 1) return;
    clearAll();
    setIsDisabled(true);
    setPage(page - 1);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setMyId((prevState) => ({ ...prevState, [name]: value }));
    if (value.length > 5) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  };

  const handleFinish = () => {
    setIsFinshed(true);
    setTimeout(() => {
      navigate("/search");
    }, 8000);
  };

  const getUserIp = async () => {
    // get gpu signature
    const canvasId = getCanvasFingerprint(myId.myId);
    // get audiio ID and pass to api
    const audioId = await getAudioFingerprint();
    // api call
    const response = await apiGetUserIp(myId.myId, { canvasId, audioId });
    setUserIp(response.data);
    // console.log(response.data);
    if (response.data.flag) setIsDisabled(false);
  };

  const getCookies = async () => {
    const response = await apiGetCookies(myId.myId);
    setCookies(response.data);
    // console.log(response.data);
    if (response.data.isComplete) setIsDisabled(false);
  };

  const getUserAgent = async () => {
    // api call
    const response = await apiGetUserAgent(myId.myId);
    setUserAgent(response.data.userAgent);
    if (response.data.uaComplete) setIsDisabled(false);
  };

  const getUniqueId = async () => {
    // api call
    const response = await apiGetUniqueId(myId.myId);
    setUniqueId(response.data.uniqueId);
    if (response.data.uuidComplete) setIsDisabled(false);
  };

  const getGpuId = async () => {
    // get gpu signature
    const canvasId = getCanvasFingerprint(myId.myId);
    // get gpu model name
    const gpuModel = getGPUModel();
    // api call
    const response = await apiGetGpuId(myId.myId, { canvasId, gpuModel });
    setGpuObject(response.data);
    if (response.data.gpuComplete) setIsDisabled(false);
  };

  const getAudioId = async () => {
    // get audiio ID and pass to api
    const audioId = await getAudioFingerprint();
    //api call
    const response = await apiGetAudioId(myId.myId, audioId);
    setAudioId(response.data);
    if (response.data.audioComplete) setIsDisabled(false);
  };

  const clearAll = () => {
    setUserIp("");
    setUserAgent("");
    setUniqueId("");
    setGpuObject({});
    setAudioId("");
    setCookies({});
  };

  // get GPU's unique signature
  const getCanvasFingerprint = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Set a specific size
    canvas.width = 200;
    canvas.height = 50;

    // 1. Draw some text with specific fonts (checks font-smoothing/hinting)
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("BrowserFingerprint1.0 <canvas> 💡", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("BrowserFingerprint1.0 <canvas> 💡", 4, 17);

    // 2. Get the unique Data URL (the GPU's "signature")
    const b64 = canvas.toDataURL();

    // 3. return base64 string to be hashed on the backend
    return b64;
  };

  const getGPUModel = () => {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return "no-gpu";

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) return "restricted";

    // Returns something like "ANGLE (NVIDIA, NVIDIA GeForce RTX 3080 Direct3D11 vs_5_0 ps_5_0, D3D11)"
    return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
  };

  const getAudioFingerprint = async () => {
    try {
      // 1. Create an offline audio context (silently renders in memory)
      const context = new (
        window.OfflineAudioContext || window.webkitOfflineAudioContext
      )(1, 44100, 44100);

      // 2. Create an oscillator (the sound source)
      const oscillator = context.createOscillator();
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(10000, context.currentTime);

      // 3. Create a compressor (this adds the unique hardware signature)
      const compressor = context.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-50, context.currentTime);
      compressor.knee.setValueAtTime(40, context.currentTime);
      compressor.ratio.setValueAtTime(12, context.currentTime);
      compressor.attack.setValueAtTime(0, context.currentTime);
      compressor.release.setValueAtTime(0.25, context.currentTime);

      // 4. Connect the nodes
      oscillator.connect(compressor);
      compressor.connect(context.destination);

      // 5. Start and render
      oscillator.start(0);
      const renderedBuffer = await context.startRendering();

      // 6. Extract the unique data from the buffer
      const rawAudioData = renderedBuffer.getChannelData(0);
      return rawAudioData
        .reduce((acc, val) => acc + Math.abs(val), 0)
        .toString();
    } catch (e) {
      console.error("Audio fingerprinting not supported", e);
    }
  };

  return (
    <div className="w-screen overflow-hidden">
      <div className="mt-5">
        <ProgressBar currentPage={page} pages={pages} />
      </div>
      {isFinished && <BalloonDrop />}

      {page === 1 && (
        <div className="w-full mt-20">
          <div className="flex justify-center text-2xl">
            Welcome to the Browser Fingerprint Gauntlet
          </div>
          <div className="flex justify-center pt-5">
            Complete each task to earn a flag.
          </div>
        </div>
      )}
      {page === 2 && (
        <div className="grid justify-items-center mt-20">
          <div>Select a Username:</div>
          <div className="grid justify-items-center h-[156px] w-52 mt-5">
            <input
              id="my-id"
              name="myId"
              type="text"
              value={myId.myId}
              onChange={handleChange}
              className="h-10 w-40 rounded-md border-1 border-gray-300 p-1"
            />
          </div>
        </div>
      )}
      {page === 3 && (
        <div className="grid justify-items-center mt-20">
          <div>Task #1: Check your IP address, then change it.</div>
          <div className="grid justify-items-center h-[156px] w-52 mt-5">
            {userIp.userIp && (
              <div>
                <div className="flex justify-center">My IP:</div>
                <div className="text-center mt-2">{userIp.userIp}</div>
                {userIp.flag && (
                  <div className="text-center mt-2">{userIp.flag}</div>
                )}
              </div>
            )}
          </div>
          <Button
            background="bg-snappily-orange"
            text="text-white"
            opacity="bg-opacity-100"
            title="Get IP"
            height="h-10"
            width="w-24"
            borderHover="hover:border-snappily-orange"
            customFunc={() => {
              getUserIp();
            }}
          />
        </div>
      )}
      {page === 4 && (
        <div className="grid justify-items-center mt-20">
          <div>Task #2: Check for Cookie.</div>
          {cookies.cookies && (
            <div className="text-center text-gray-500 px-10">
              Would you like some milk with those cookies? 🥛🍪
            </div>
          )}
          <div className="grid justify-items-center h-[156px] w-[600px] mt-5">
            {cookies.cookies && (
              <div>
                <div className="flex justify-center text-lg">Your Cookies:</div>
                <div className="text-center break-all mt-2">
                  {cookies.cookies}
                </div>
              </div>
            )}
            {cookies.isComplete && (
              <div className="mt-5">
                <div className="flex justify-center">
                  No Cookies, Great Job!
                </div>
                <div className="flex justify-center mt-5">{cookies.flag}</div>
              </div>
            )}
          </div>
          <Button
            background="bg-snappily-orange"
            text="text-white"
            opacity="bg-opacity-100"
            title="Get 🍪's"
            height="h-10"
            width="w-24"
            borderHover="hover:border-snappily-orange"
            customFunc={() => {
              getCookies();
            }}
          />
        </div>
      )}
      {page === 5 && (
        <div className="grid justify-items-center mt-20">
          <div>Task #3: Check your 'User-Agent' header, then change it.</div>
          {userAgent && (
            <div className="text-center text-gray-500 px-10">
              Hint: send a GET request to
              {userAgent
                ? ` ${BASE_URL}/fingerprints/user-agent/${encodeURIComponent(myId.myId)}`
                : ""}
            </div>
          )}
          <div className="grid justify-items-center h-[156px] w-[300px] mt-5">
            {userAgent && (
              <div>
                <div className="flex justify-center text-lg">User Agent:</div>
                <div className="text-center break-all mt-2">{userAgent}</div>
              </div>
            )}
          </div>
          <Button
            background="bg-snappily-orange"
            text="text-white"
            opacity="bg-opacity-100"
            title="Get UA"
            height="h-10"
            width="w-24"
            borderHover="hover:border-snappily-orange"
            customFunc={() => {
              getUserAgent();
            }}
          />
        </div>
      )}
      {page === 6 && (
        <div className="grid justify-items-center mt-20">
          <div>Task #3: Check your Unique User ID, then change it.</div>
          {uniqueId && (
            <div className="text-center text-gray-500 px-10">
              Hint: send a GET request to
              {uniqueId
                ? ` ${BASE_URL}/fingerprints/uuid/${encodeURIComponent(myId.myId)}`
                : ""}
            </div>
          )}
          <div className="grid justify-items-center h-[156px] w-52 mt-5">
            {uniqueId && (
              <div>
                <div className="flex justify-center text-lg">
                  Unique User ID (IP + UA):
                </div>
                <div className="text-center break-all mt-2">{uniqueId}</div>
              </div>
            )}
          </div>
          <Button
            background="bg-snappily-orange"
            text="text-white"
            opacity="bg-opacity-100"
            title="Get UUID"
            height="h-10"
            width="w-24"
            borderHover="hover:border-snappily-orange"
            customFunc={() => {
              getUniqueId();
            }}
          />
        </div>
      )}
      {page === 7 && (
        <div className="grid justify-items-center mt-20">
          <div>Task #4: Change your GPU Signature.</div>
          {gpuObject.id && (
            <div className="text-center text-gray-500 px-10">
              Hint: send a GET request to
              {gpuObject.id
                ? ` ${BASE_URL}/fingerprints/gpu-id/${encodeURIComponent(myId.myId)}`
                : ""}
            </div>
          )}
          <div className="flex justify-center gap-x-5 h-[156px] mt-5">
            {gpuObject.id && (
              <>
                <div className="grid justify-items-center w-60">
                  <div>GPU Details:</div>
                  <div className="text-center break-all">{gpuObject.model}</div>
                </div>
                <div className="grid justify-items-center w-60">
                  <div>GPU Signature:</div>
                  <div className="text-center break-all">{gpuObject.id}</div>
                </div>
              </>
            )}
          </div>
          <Button
            background="bg-snappily-orange"
            text="text-white"
            opacity="bg-opacity-100"
            title="Get GPU"
            height="h-10"
            width="w-24"
            borderHover="hover:border-snappily-orange"
            customFunc={() => {
              getGpuId();
            }}
          />
        </div>
      )}
      {page === 8 && (
        <div className="grid justify-items-center mt-20">
          <div>Task #5: Change your Audio Signature.</div>
          {audioId.audioId && (
            <div className="text-center text-gray-500 px-10">
              Hint: send a GET request to
              {audioId.audioId
                ? ` ${BASE_URL}/fingerprints/audio-id/${encodeURIComponent(myId.myId)}`
                : ""}
            </div>
          )}
          <div className="flex justify-center gap-x-5 h-[156px] mt-5">
            {audioId.audioId && (
              <div className="grid justify-items-center w-60">
                <div>Audio Signature:</div>
                <div className="text-center break-all">{audioId.audioId}</div>
              </div>
            )}
          </div>
          <Button
            background="bg-snappily-orange"
            text="text-white"
            opacity="bg-opacity-100"
            title="Get Audio"
            height="h-10"
            width="w-24"
            borderHover="hover:border-snappily-orange"
            customFunc={() => {
              getAudioId();
            }}
          />
        </div>
      )}
      {page > 2 && (
        <div className="flex justify-center mt-5">
          <Button
            background="bg-snappily-orange"
            text="text-white"
            opacity="bg-opacity-100"
            title="clear"
            height="h-10"
            width="w-20"
            borderHover="hover:border-snappily-orange"
            customFunc={() => {
              clearAll();
            }}
          />
        </div>
      )}
      <div className="flex justify-center w-full mt-20">
        {page > 1 && (
          <Button
            background="bg-slate-400"
            text="text-white"
            opacity="bg-opacity-100"
            title="Back"
            height="h-10"
            width="w-24"
            borderColor="border-none"
            borderHover="hover:border-snappily-orange"
            customFunc={prevPage}
          />
        )}
        <Button
          background="bg-snappily-orange"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === 1 ? "Begin" : page === pages ? "Done" : "Next"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          isDisabled={page === 1 ? false : isDisabled}
          customFunc={page === pages ? handleFinish : nextPage}
        />
      </div>
    </div>
  );
};

export default Fingerprint;
